import { PageFormToolbar } from '../../../components-common/layout/views/PageFormToolbar';
import { ComponentTypeScreenTabs } from '../../../components-domain/bim-domain/elements/component-type-creation/ComponentTypeScreenTabs';
import { IndexContainer } from '../../../components-common/containers/IndexContainer';
import {
  editParameterScreen,
  newParameterScreen,
  parametersTab,
  toolbar_components,
} from './_common_props';
import { TabbedPage } from '../../../components-common/tabs/TabbedPage';
import { layoutWorkspace } from '../../../_website/_common/layout.workspace';
import { clone } from 'ramda';
import { getWallStructureSchema } from '../../../components-domain/bim-domain/elements/component-type-creation/schema';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

/**
 * Configuration Object: typesFormScreen
 *
 * This object defines the configuration for the types form screen.
 * It specifies the component, form, template, validation schema, and initial empty values.
 *
 * @property {React.Component} Component - The component to render the form toolbar.
 * @property {React.Component} form - The form component used for managing component type screen tabs.
 * @property {string} template - The template URL for the parameters.
 * @property {Object} schema - The validation schema for the form.
 * @property {Object} empty - The initial empty values for the form fields.
 */
const typesFormScreen = {
  Component: PageFormToolbar,
  form: ComponentTypeScreenTabs,
  template: '/buildingms/categories/parameters',
  schema: getWallStructureSchema(),
  empty: {
    name: '',
    description: '',
    side: 'INTERIOR',
    category: 'WALLS',
    layers: { core: [], interior: [], exterior: [] },
    typeParameters: [],
  },
};

/**
 * Array: wallsTabs
 *
 * This array contains configuration objects for different tabs in the walls section.
 * Each tab includes an ID, title, and components to be rendered within the tab.
 *
 * @property {string} id - The ID of the tab.
 * @property {string} title - The title of the tab.
 * @property {Array} components - An array of components to be rendered within the tab.
 */
const wallsTabs = [
  {
    id: 'walls_types',
    title: 'Types',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            ...toolbar_components,
            left: [
              {
                id: 'create',
                kind: 'create',
                onAction: (props: any) => props.navigate('types/new'),
              } as any,
            ],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'table_components',
            props: {
              source: {
                type: 'REMOTE',
                url: '/buildingsms/types/elements',
                key: '/buildingsms/types/elements',
                urlParams: { category: 'WALLS' },
              },
              columns: [
                'name',
                'description',
                { id: 'sourceType' },
                'createdDate',
                'modifiedDate',
              ],
              actions: [
                {
                  id: 'edit',
                  onClick: (props: any) =>
                    props.navigate(`types/${props.row.id}`),
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Edit type',
                },
                {
                  id: 'copy',
                  actionToDispatch: {
                    payload: {
                      copy: '/buildingsms/types/elements/cloneElement',
                    },
                  },
                  displayTooltip: true,
                  tooltipText: 'Duplicate type',
                } as any,
                {
                  id: 'delete',
                  actionToDispatch: {
                    payload: {
                      endpointToDelete: '/buildingsms/types/elements',
                    },
                  },
                  additionalChecks: compDBAdditionalCheck,
                  displayTooltip: true,
                  tooltipText: 'Delete type',
                },
              ],
            },
          },
        },
      },
    ],
  },
  parametersTab('WALLS'),
];

/**
 * Walls Section Configuration
 *
 * Defines the configuration for the walls section, including paths, roles, and templates for various screens.
 *
 * @type {ConfigSection}
 * @property {string} id - The unique identifier for the section.
 * @property {string} path - The base path for the section.
 * @property {Array} screens - The array of screen configurations within the section.
 */
export const wallsSection: any = {
  id: 'walls',
  title: 'Walls',
  path: 'walls',
  tmp: 'tabbed',
  layout: layoutWorkspace,
  Component: TabbedPage,
  screens: [
    {
      id: 'walls',
      path: '',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tabs: wallsTabs,
    },
    {
      id: 'types_create',
      title: 'Create New Wall Type',
      path: 'types/new',
      tmp: 'create',
      left: ['create'],
      right: ['back'],
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      ...clone(typesFormScreen),
    },
    {
      id: 'types_edit',
      path: 'types/:wallTypeId',
      title: 'Edit Wall Type',
      tmp: 'edit',
      left: ['save'],
      right: ['back'],
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      editSource: '/buildingsms/types/elements/walls/',
      idPropSource: 'wallTypeId',
      ...clone(typesFormScreen),
    },
    newParameterScreen('WALLS'),
    editParameterScreen('WALLS'),
  ],
};
